import React, { useEffect, useMemo, useRef, useState } from 'react'
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom'
import { toAbsoluteUrl } from '../../../../_metronic/helpers'
import { useAuth } from '../../../modules/auth/core/Auth'
import NotificationBell from '../../Counselling/shared/NotificationBell'
import { STUDENT_MENU_ITEMS } from './studentMenuConfig'
import { StudentDataProvider, useStudentData } from './StudentDataContext'
import { PROFILE_LOCK_MESSAGE, PROFILE_PATH, profileIncomplete } from './studentProfileGate'
import './StudentPortalLayout.css'

/**
 * Dedicated student portal shell — green glassmorphism theme.
 *
 * Decoupled from the admin Metronic MasterLayout. A student who signs in via the
 * unified /auth page (student mode) lands here and only ever loads this lightweight
 * shell. Navigation comes from the single source of truth STUDENT_MENU_ITEMS
 * (studentMenuConfig.tsx) — content unchanged; only the look is the new green/glass
 * theme (palette borrowed from StudentPortalDashboard's spd-* design).
 *
 * Dashboard data is fetched ONCE by the StudentDataProvider that wraps this shell
 * (so it persists across in-portal navigation), and pages read it via useStudentData().
 * The top-bar Refresh button re-runs that bootstrap on demand.
 *
 * Until the one-time profile step is done, every portal page but My Info is locked in
 * the menu ("Complete your profile first"); picking one opens the profile form, which
 * returns the student to that page once saved. The top bar carries the one
 * notification bell for every page.
 *
 * Phones (768px and below): the menu is a drawer that starts closed, slides in over a
 * backdrop, and closes on a tap outside, on Escape and on moving to another page.
 */

const HamburgerIcon = () => (
  <svg width='18' height='18' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2' strokeLinecap='round'>
    <line x1='3' y1='6' x2='21' y2='6' />
    <line x1='3' y1='12' x2='21' y2='12' />
    <line x1='3' y1='18' x2='21' y2='18' />
  </svg>
)
const LockIcon = () => (
  <svg width='13' height='13' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2' strokeLinecap='round' aria-hidden='true'>
    <rect x='4' y='11' width='16' height='10' rx='2' /><path d='M8 11V7a4 4 0 0 1 8 0v4' />
  </svg>
)
const SignOutIcon = () => (
  <svg width='17' height='17' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2' strokeLinecap='round'>
    <path d='M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4' /><polyline points='16 17 21 12 16 7' /><line x1='21' y1='12' x2='9' y2='12' />
  </svg>
)
const RefreshIcon = () => (
  <svg width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round'>
    <polyline points='23 4 23 10 17 10' /><polyline points='1 20 1 14 7 14' />
    <path d='M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15' />
  </svg>
)

// Matches the CSS breakpoint where the aside becomes an overlay drawer.
const isPhoneViewport = () =>
  typeof window !== 'undefined' && !!window.matchMedia && window.matchMedia('(max-width: 768px)').matches

const StudentPortalShell: React.FC = () => {
  const navigate = useNavigate()
  const { pathname } = useLocation()
  const { currentUser } = useAuth()
  const { refresh, loading: dataLoading } = useStudentData()
  // On phones the aside overlays the page, so it starts closed there.
  const [collapsed, setCollapsed] = useState(isPhoneViewport)
  const menuButtonRef = useRef<HTMLButtonElement>(null)
  const locked = profileIncomplete(currentUser)

  // Phones: close the drawer after picking a page.
  useEffect(() => {
    if (isPhoneViewport()) setCollapsed(true)
  }, [pathname])

  // Phones: Escape closes an open drawer and hands focus back to the menu button.
  useEffect(() => {
    if (collapsed) return
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Escape' || !isPhoneViewport()) return
      setCollapsed(true)
      menuButtonRef.current?.focus()
    }
    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [collapsed])

  useEffect(() => {
    // Force light theme for the student portal; defensively hide the Metronic splash.
    document.documentElement.setAttribute('data-theme', 'light')
    localStorage.setItem('kt_theme_mode_value', 'light')
    const splash = document.getElementById('splash-screen')
    if (splash) splash.style.display = 'none'
    document.body.classList.remove('page-loading', 'splash-screen')
  }, [])

  const displayName =
    (currentUser as any)?.name ||
    (currentUser as any)?.studentName ||
    currentUser?.email ||
    'Student'

  const initials = useMemo(() => {
    const src = String(displayName).trim()
    if (!src || src.includes('@')) return (src[0] || 'S').toUpperCase()
    return src
      .split(/\s+/)
      .slice(0, 2)
      .map((p) => p[0])
      .join('')
      .toUpperCase()
  }, [displayName])

  // Breadcrumb leaf = the menu item whose route best matches the current path.
  // The dashboard root reads "Home" so it doesn't render "Dashboard › Dashboard".
  const currentLabel = useMemo(() => {
    const match = STUDENT_MENU_ITEMS
      .filter((i) => !i.externalHref && pathname.startsWith(i.path))
      .sort((a, b) => b.path.length - a.path.length)[0]
    const label = match?.label || 'Dashboard'
    return label === 'Dashboard' ? 'Home' : label
  }, [pathname])

  // Real sign-out: the shared /logout route revokes the session server-side and
  // bounces to /auth/login. (Never just clear localStorage.)
  const handleLogout = () => navigate('/logout')

  // A locked page opens the profile form, which comes back to that page once saved.
  const openLocked = (path: string) => {
    if (isPhoneViewport()) setCollapsed(true)
    navigate(PROFILE_PATH, { replace: pathname === PROFILE_PATH, state: { from: path } })
  }

  const userId = currentUser?.id ?? 0

  return (
    <div className='sp-shell'>
      {/* Aside */}
      <aside id='sp-aside' className={`sp-aside ${collapsed ? 'collapsed' : ''}`}>
        <div className='sp-brand'>
          <img className='sp-brand-logo' src={toAbsoluteUrl('/media/logos/kcc.webp')} alt='Career-9' />
          <div className='sp-brand-text'>
            <span>Student Portal</span>
          </div>
        </div>

        {locked && <p className='sp-nav-note'>{PROFILE_LOCK_MESSAGE} to open these pages.</p>}

        <nav className='sp-nav'>
          {STUDENT_MENU_ITEMS.map((item) =>
            locked && !item.externalHref && item.path !== PROFILE_PATH ? (
              <button
                key={item.path}
                type='button'
                className='sp-nav-item locked'
                aria-disabled='true'
                aria-label={`${item.label}: ${PROFILE_LOCK_MESSAGE.toLowerCase()}`}
                title={PROFILE_LOCK_MESSAGE}
                onClick={() => openLocked(item.path)}
              >
                <span className='sp-nav-icon'>{item.icon}</span>
                <span className='sp-nav-label'>{item.label}</span>
                <span className='sp-nav-lock'><LockIcon /></span>
              </button>
            ) : item.externalHref ? (
              <a
                key={item.path}
                href={item.externalHref}
                target='_blank'
                rel='noopener noreferrer'
                className='sp-nav-item'
                title={item.label}
              >
                <span className='sp-nav-icon'>{item.icon}</span>
                <span className='sp-nav-label'>{item.label}</span>
              </a>
            ) : (
              <NavLink
                key={item.path}
                to={item.path}
                // Dashboard root must match exactly or it stays active on every sub-page.
                end={item.path === '/student/dashboard'}
                className={({ isActive }) => `sp-nav-item ${isActive ? 'active' : ''}`}
                title={item.label}
              >
                <span className='sp-nav-icon'>{item.icon}</span>
                <span className='sp-nav-label'>{item.label}</span>
              </NavLink>
            )
          )}
        </nav>

        <div className='sp-aside-bottom'>
          <div className='sp-user'>
            <div className='sp-avatar'>{initials}</div>
            <div className='sp-user-meta'>
              <strong title={displayName}>{displayName}</strong>
              <span>Student</span>
            </div>
          </div>
        </div>
      </aside>
      {!collapsed && <div className='sp-backdrop' onClick={() => setCollapsed(true)} aria-hidden='true' />}

      {/* Body */}
      <div className='sp-body'>
        <header className='sp-topbar'>
          <div className='sp-topbar-left'>
            <button
              ref={menuButtonRef}
              className='sp-icon-btn'
              onClick={() => setCollapsed((c) => !c)}
              title={collapsed ? 'Expand menu' : 'Collapse menu'}
              aria-label='Toggle menu'
              aria-controls='sp-aside'
              aria-expanded={!collapsed}
            >
              <HamburgerIcon />
            </button>
            <div className='sp-breadcrumb'>
              <span className='sp-breadcrumb-root'>Dashboard <span aria-hidden>›</span></span>
              <b title={currentLabel}>{currentLabel}</b>
            </div>
          </div>

          <div className='sp-topbar-right'>
            {/* The one bell: unread count and the list, on every page. */}
            {userId > 0 && (
              <div className='sp-topbar-bell'>
                <NotificationBell userId={userId} />
              </div>
            )}
            <button
              className='sp-icon-btn'
              onClick={() => refresh()}
              disabled={dataLoading}
              title='Refresh dashboard data'
              aria-label='Refresh dashboard data'
            >
              <span className={dataLoading ? 'sp-spin' : ''} style={{ display: 'inline-flex' }}>
                <RefreshIcon />
              </span>
            </button>
            <button className='sp-icon-btn' onClick={handleLogout} title='Sign out' aria-label='Sign out'>
              <SignOutIcon />
            </button>
          </div>
        </header>

        <main className='sp-content'>
          <Outlet />
        </main>
      </div>
    </div>
  )
}

/**
 * Wraps the shell in the StudentDataProvider so the dashboard data is bootstrapped
 * once and shared with every page (and the Refresh button) for the whole session.
 */
const StudentPortalLayout: React.FC = () => (
  <StudentDataProvider>
    <StudentPortalShell />
  </StudentDataProvider>
)

export default StudentPortalLayout
